/**
 * Mirrors the back end's `SearchQueryValidator`, rule for rule, so the field
 * and the API agree on every term: anything shown as fine here is accepted
 * there. Change one, change the other.
 *
 * The C# uses lookbehinds — `(?<!\d)`, `(?<![\w+])`. Safari before 16.4 fails
 * to parse a lookbehind at all, which would take the whole bundle down with it,
 * so each one is written as "start of string or a character that is not a
 * digit" instead. A match then includes that one leading character, which
 * every check below ignores — they only look at whether there is a match, or at
 * its digits.
 */

// name@domain.tld
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

// 13–19 digits, optionally separated by single spaces or dashes
const CARD_CANDIDATE =
  /(?:^|\D)(\d{13,19}|\d{4}[ -]\d{4}[ -]\d{4}[ -]\d{1,7}|\d{4}[ -]\d{6}[ -]\d{4,5})(?!\d)/g;

// +40 722 123 456, +44 (0)20 7946 0958, 0040722123456
const INTL_PHONE = /(?:^|[^\w+])(?:\+|00)[\s.-]?\d{1,3}(?:[\s.\-()]*\d){6,12}(?!\d)/;

// National formats that start with a trunk 0: 0722123456, 0722 123 456,
// 020 7946 0958, 07911 123456
const NATIONAL_PHONE =
  /(?:^|\D)(?:0\d{9,10}|0\d{2,4}[\s.-]\d{3,4}[\s.-]\d{3,4}|0\d{3,4}[\s.-]\d{6})(?!\d)/;

// US/CA style: (555) 123-4567, 555-123-4567, 555.123.4567
const US_PHONE =
  /(?:^|\D)(?:\(\d{3}\)[\s.-]?\d{3}[\s.-]\d{4}|\d{3}[\s.-]\d{3}[\s.-]\d{4})(?!\d)/;

function passesLuhn(digits: string): boolean {
  let sum = 0;
  let alternate = false;

  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = digits.charCodeAt(i) - 48;

    if (alternate) {
      n *= 2;
      if (n > 9) n -= 9;
    }

    sum += n;
    alternate = !alternate;
  }

  return sum % 10 === 0;
}

function containsEmail(value: string): boolean {
  return EMAIL.test(value);
}

function containsCreditCard(value: string): boolean {
  // A fresh copy per call: a shared /g regex keeps `lastIndex` between calls.
  const candidates = new RegExp(CARD_CANDIDATE.source, "g");
  let match = candidates.exec(value);

  while (match) {
    const digits = match[1].replace(/\D/g, "");

    if (digits.length >= 13 && digits.length <= 19 && passesLuhn(digits)) {
      return true;
    }

    // A match ends before the non-digit that follows it, so the next search
    // starts on that character and it can serve as the next one's lead-in.
    match = candidates.exec(value);
  }

  return false;
}

function containsPhone(value: string): boolean {
  return (
    INTL_PHONE.test(value) ||
    NATIONAL_PHONE.test(value) ||
    US_PHONE.test(value)
  );
}

export const checkIsStringSensitiveData = (value: string): boolean => {
  if (!value || !value.trim()) {
    return false;
  }

  return containsEmail(value) || containsCreditCard(value) || containsPhone(value);
};
